import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { notify } from '../../notifications';
import { ConfirmDialog } from '../reports/edit/EditCommon';
import { CleanupPreview, auditErrorMessage, fetchCleanupPreview, runAuditCleanup } from './auditApi';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Очистка выполнена: экран перечитывает журнал */
  onCleaned: () => void;
}

/** Очистка журнала (только ранг 100): удаляются записи старше срока хранения, свежие не затрагиваются */
const AuditCleanupDialog: React.FC<Props> = ({ open, onClose, onCleaned }) => {
  const [preview, setPreview] = useState<CleanupPreview | null>(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setPreview(null);
    setError('');
    fetchCleanupPreview()
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch((err) => {
        if (!cancelled) setError(auditErrorMessage(err, 'Не удалось получить объем очистки'));
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleConfirm = async () => {
    if (running) return;
    setRunning(true);
    try {
      const deleted = await runAuditCleanup();
      notify.success(deleted > 0 ? `Удалено записей: ${deleted}` : 'Устаревших записей нет');
      onCleaned();
      onClose();
    } catch (err) {
      notify.error(auditErrorMessage(err, 'Не удалось выполнить очистку'));
    } finally {
      setRunning(false);
    }
  };

  const expired = preview ? preview.expired : 0;

  return (
    <ConfirmDialog
      open={open}
      title="Очистка журнала действий"
      confirmText="Удалить"
      confirmColor="error"
      loading={running}
      confirmDisabled={!preview || expired === 0}
      maxWidth="xs"
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      {error && <Typography sx={{ color: '#b91c1c', fontSize: '0.88rem' }}>{error}</Typography>}
      {!error && !preview && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={26} />
        </Box>
      )}
      {preview && (
        <>
          <Typography sx={{ fontSize: '0.9rem', color: '#334155' }}>
            Срок хранения записей: {preview.retentionDays} дн. Старше этого срока в журнале записей: <b>{preview.expired}</b>.
          </Typography>
          <Typography sx={{ fontSize: '0.84rem', color: '#64748b', mt: 1.5 }}>
            {preview.expired > 0
              ? 'Будут удалены только устаревшие записи, остальные не изменятся. Действие нельзя отменить, но сама очистка запишется в журнал.'
              : 'Удалять нечего: все записи в пределах срока хранения.'}
          </Typography>
        </>
      )}
    </ConfirmDialog>
  );
};

export default AuditCleanupDialog;
